import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

const KesarProduct = () => {
  const saffronFeatures = [
    'Grade A+ Premium Quality',
    'Sourced from Kashmir Valley',
    '100% Pure and Natural',
    'No Artificial Colors or Additives',
    'Rich Aroma and Deep Color',
    'Carefully Hand-Picked',
    'Lab Tested for Purity',
    'Authentic Indian Origin'
  ];

  return (
    <div className="pt-20">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-saffron-50 to-cream-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">Premium Kashmiri Kesar</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experience the world's finest saffron, sourced from the pristine valleys of Kashmir
            </p>
          </div>
        </div>
      </section>

      {/* Product Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Product Image */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1760982685244-17a1814ab806"
                  alt="Premium Kashmiri Saffron"
                  className="rounded-2xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-6 -right-6 bg-saffron-600 text-white px-6 py-3 rounded-xl shadow-lg">
                  <p className="text-sm font-semibold">100% Pure</p>
                </div>
              </div>
            </div>

            {/* Product Details */}
            <div className="order-1 lg:order-2">
              <div className="inline-block bg-saffron-100 text-saffron-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Featured Product
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Premium Kashmiri Saffron</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our signature product - the world's finest saffron from the pristine valleys of Kashmir. 
                Known for its deep color, rich aroma, and unmatched quality, this premium grade saffron 
                is carefully hand-picked and processed to preserve its natural properties.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Perfect for culinary excellence, traditional medicine, and adding a touch of luxury to 
                your daily life. Each strand is a testament to centuries of tradition and expertise.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {saffronFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-5 h-5 bg-saffron-100 rounded-full flex items-center justify-center mt-0.5">
                      <Check className="text-saffron-600" size={14} />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-saffron-600 text-white font-semibold rounded-lg hover:bg-saffron-700 transition-all duration-300 hover:shadow-lg hover:scale-105"
              >
                Contact Us for Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Why Our Saffron Stands Out</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Origin & Authenticity</h4>
                <p className="text-gray-600 leading-relaxed">
                  Sourced directly from Kashmir's renowned saffron fields, where the unique climate 
                  and soil produce the world's finest saffron threads.
                </p>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Processing & Care</h4>
                <p className="text-gray-600 leading-relaxed">
                  Hand-picked at dawn and carefully dried using traditional methods to preserve 
                  maximum flavor, aroma, and medicinal properties.
                </p>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Quality Assurance</h4>
                <p className="text-gray-600 leading-relaxed">
                  Every batch is rigorously tested for purity and quality, ensuring you receive 
                  only the finest Grade A+ saffron threads.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Health Benefits Section - MOVED ABOVE Purity Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Health Benefits of Pure Kesar</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Clinically proven wellness benefits backed by modern science.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Benefit 1 - Happier Mind */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#happier-mind"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1687436874774-fa0d4616bec9"
                  alt="Mental Wellness"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Happier Mind, Naturally</h3>
                <p className="text-gray-600 leading-relaxed">
                  Clinically proven to improve mood and reduce symptoms of mild-to-moderate depression comparable to standard antidepressants.
                </p>
              </div>
            </Link>

            {/* Benefit 2 - Sharper Memory */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#sharper-memory"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1753617868907-d1200a990ec5"
                  alt="Cognitive Health"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Sharper Memory & Focus</h3>
                <p className="text-gray-600 leading-relaxed">
                  Shown in human trials to enhance memory, learning, and cognitive performance, including support in age-related decline.
                </p>
              </div>
            </Link>

            {/* Benefit 3 - Stress Relief */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#stress-relief"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1753617868995-76b0e3748a27"
                  alt="Stress Relief"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Stress & Anxiety Relief</h3>
                <p className="text-gray-600 leading-relaxed">
                  Demonstrated to lower anxiety levels and promote calm by balancing stress-related neurotransmitters.
                </p>
              </div>
            </Link>

            {/* Benefit 4 - Heart Health */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#heart-health"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1743187360550-ee62755a1118"
                  alt="Heart Health"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Heart-Healthy Protection</h3>
                <p className="text-gray-600 leading-relaxed">
                  Clinically shown to reduce LDL oxidation and improve antioxidant status, supporting healthier blood vessels and circulation.
                </p>
              </div>
            </Link>

            {/* Benefit 5 - PMS Comfort */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#pms-comfort"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1573461160327-b450ce3d8e7f"
                  alt="Women's Health"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">PMS & Menstrual Comfort</h3>
                <p className="text-gray-600 leading-relaxed">
                  Proven to significantly reduce PMS symptoms like cramps, mood swings, and irritability in women.
                </p>
              </div>
            </Link>

            {/* Benefit 6 - Radiant Skin */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#radiant-skin"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1709004157726-cb9df09bfce5"
                  alt="Radiant Skin"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Radiant Skin From Within</h3>
                <p className="text-gray-600 leading-relaxed">
                  Rich in potent antioxidants that protect cells from oxidative damage, helping maintain clearer, healthier-looking skin.
                </p>
              </div>
            </Link>

            {/* Benefit 7 - Immunity Support */}
            <Link
              to="/articles/pure-kashmiri-kesar-clinically-proven-elixir#immunity-support"
              className="bg-cream-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1633457573560-7d18d03af642"
                  alt="Immunity Support"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Stronger Immunity Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  Scientifically validated antioxidant and anti-inflammatory activity helps strengthen the body's natural defense system.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* How to Check Purity Section - MOVED BELOW Health Benefits */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How to Check the Purity of Kesar</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Learn how to identify authentic saffron and ensure you're getting the best quality product.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Step 1 - Water Test */}
            <div className="bg-cream-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1613549026660-6c5c7a9cae1e"
                  alt="Saffron Water Test"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex items-center mb-3">
                <span className="w-8 h-8 bg-saffron-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">1</span>
                <h3 className="text-xl font-bold text-gray-900">Water Test</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Place a few strands in warm water. Pure saffron releases color slowly while the threads keep their shape.
              </p>
            </div>

            {/* Step 2 - Color Check */}
            <div className="bg-cream-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1560076124-fe336393ef76"
                  alt="Saffron Color Check"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex items-center mb-3">
                <span className="w-8 h-8 bg-saffron-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">2</span>
                <h3 className="text-xl font-bold text-gray-900">Color Check</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Authentic saffron is deep red with slightly orange tips. Avoid bright yellow or overly uniform strands.
              </p>
            </div>

            {/* Step 3 - Aroma Test */}
            <div className="bg-cream-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1709004157726-cb9df09bfce5"
                  alt="Saffron Aroma Test"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex items-center mb-3">
                <span className="w-8 h-8 bg-saffron-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">3</span>
                <h3 className="text-xl font-bold text-gray-900">Aroma Test</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Pure saffron has a strong natural aroma — earthy and honey-like, not artificial or sweet.
              </p>
            </div>

            {/* Step 4 - Taste Test */}
            <div className="bg-cream-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1709004789083-139ce217f87f"
                  alt="Saffron Taste Test"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex items-center mb-3">
                <span className="w-8 h-8 bg-saffron-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">4</span>
                <h3 className="text-xl font-bold text-gray-900">Taste Test</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Real saffron tastes slightly bitter, never sweet.
              </p>
            </div>

            {/* Step 5 - Thread Structure */}
            <div className="bg-cream-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1643471672168-f4a4b6cfa440"
                  alt="Saffron Thread Structure"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex items-center mb-3">
                <span className="w-8 h-8 bg-saffron-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">5</span>
                <h3 className="text-xl font-bold text-gray-900">Thread Structure</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Original strands are trumpet-shaped and uneven, not perfectly straight or identical.
              </p>
            </div>
          </div>

          {/* Guarantee Statement */}
          <div className="bg-gradient-to-br from-saffron-50 to-cream-100 rounded-2xl p-8 md:p-12 text-center">
            <p className="text-2xl font-bold text-gray-900 mb-2">
              HindHarvest guarantees 100% pure, lab-tested saffron sourced directly from trusted farms.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KesarProduct;
